/**
 * delist-reason-error.ts — `BOOK_DELIST_REASON_REQUIRED` 的中文可执行提示。
 *
 * 该码在三层写入口都可能出现，但**只有层① 书组默认层写**会带
 * `details.missing_reason_novel_ids`：层① 落 `DELISTED` 时按该书组**每个** active 语言版本各自
 * 校验 `status_reason_i18n.en`，任一行缺失即拒绝整笔（A6 §12.1）。整笔被拒时运营必须知道**是哪几行**
 * 缺原因，否则只能逐个语言版本试。
 *
 * 契约：`02-API契约/A6-书籍域.md` §12（共享判据「`DELISTED` reason 前置」）/ §12.1（逐行校验、
 * 整笔原子 + `details.missing_reason_novel_ids`）。
 */

import { AdminApiError } from '~/api/errors';

const BOOK_DELIST_REASON_REQUIRED = 'BOOK_DELIST_REASON_REQUIRED';

/**
 * 把 `missing_reason_novel_ids` 映射成运营能认的行标识。
 *
 * novel_id 是裸 uuid，后台界面本就不展示它（A6 §15.1「界面标识规则」）—— 直接把 id 列出来运营
 * 认不出是哪一行。故按调用方给的 `resolveRow` 解析成「语言 + 书编码」，解析不到才回退 id 尾段。
 */
export function delistReasonRequiredErrorMessage(
  err: unknown,
  options: {
    /** novel_id → 行标识（如「PL · k7m2qpv001」）；返回 null 表示解析不到 */
    resolveRow?: (novelId: string) => string | null;
    /** 缺 `details` 时的兜底动作提示（层② / 层③ 写不带 details）*/
    fallbackHint?: string;
  } = {},
): string | null {
  if (!(err instanceof AdminApiError) || err.code !== BOOK_DELIST_REASON_REQUIRED) return null;

  const raw = err.details?.missing_reason_novel_ids;
  const ids = Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
  if (ids.length === 0) {
    return (
      options.fallbackHint ??
      '下架前必须先填写英文下架原因（status_reason_i18n.en），本次未写入任何改动。'
    );
  }
  const rows = ids.map((id) => options.resolveRow?.(id) ?? id.slice(0, 8));
  return `下架未生效：该书组有 ${rows.length} 个语言版本仍缺英文下架原因（${rows.join('、')}）。书组默认层落下架要求每个语言版本各自有原因，整笔已回滚、未写入任何改动。`;
}
